import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseButtonSizeEnum } from '../base-button/base-button-size.enum';
import { BatchAddMemberFormRow } from './types/batch-add-member-form-row';
import { FormArray } from '@angular/forms';
import { CreateMemberRequest } from '../../../../models/users/requests/create-member-request';
import { DateConversionUtils } from '../../../../utils/date-conversion-utils';
import { BatchAddMemberFormGroup } from './types/batch-add-member-form-group';
import { BatchAddMembersFormFactory } from './utils/batch-add-members-form-factory';
import { NewMemberVerificationRequest } from '../../../../models/members/new-member-verification-request';
import { BatchAddMembersSubmission } from './types/batch-add-members-submission';

@Component({
  selector: 'app-batch-add-members-information-table-card',
  templateUrl: './batch-add-members-information-table-card.component.html',
  styleUrls: ['./batch-add-members-information-table-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchAddMembersInformationTableCardComponent {
  @Input() rows!: FormArray<BatchAddMemberFormRow>;
  @Input() cardTitle: string = '';
  @Input() description: string = '';
  @Input() primaryButtonText: string = $localize`Continue`;
  @Input() isLoading: boolean = false;

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() tableSubmitted = new EventEmitter<BatchAddMembersSubmission>();

  public checkEmploymentStartDate(val: string, rowIndex: number): void {
    const completeDate = this.isMaskedDateComplete(val);
    if (completeDate) {
      const rowData = this.rows.at(rowIndex);
      rowData.submitAttempted = true;
    }
  }

  private isMaskedDateComplete(maskedDate: string | null | undefined): boolean {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return !!maskedDate && maskedDate.trim() !== '' && dateRegex.test(maskedDate);
  }

  public addRow(): void {
    this.rows.insert(0, BatchAddMembersFormFactory.initializeMemberFormRow());
  }

  public removeRow(rowIndex: number): void {
    this.rows.removeAt(rowIndex);
  }

  public rowHasError(index: number): boolean {
    const form = this.rows.at(index);
    if (!form) return false;
    return !form.valid && !!form.submitAttempted;
  }

  public onRowBlur(event: FocusEvent, rowIndex: number): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const rowElement = event.currentTarget as HTMLElement;
    const row = this.rows.at(rowIndex);
    const rowDeleted = relatedTarget?.classList?.contains('delete-button');
    if (!rowElement.contains(relatedTarget) && !rowDeleted) {
      row.submitAttempted = true;
    }
  }

  private formatDateOnlyForBackend(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const date = DateConversionUtils.shorthandToDate(dateStr);
    return date ? DateConversionUtils.dateToNativeInput(date) : '';
  }

  private getEmploymentHistory(row: BatchAddMemberFormGroup) {
    return {
      startDate: this.formatDateOnlyForBackend(row.value.employmentStartDate)
    };
  }

  private buildVerificationRequest(row: BatchAddMemberFormGroup): NewMemberVerificationRequest {
    const value = row.getRawValue();
    return Object.assign(new NewMemberVerificationRequest(), {
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      email: value.email.trim(),
      birthDate: this.formatDateOnlyForBackend(value.dateOfBirth),
      socialInsuranceNumber: value.socialInsuranceNumber.trim()
    });
  }

  public submitTable(): void {
    if (this.rows.controls.some(row => row.invalid)) {
      this.rows.controls.forEach(row => {
        row.submitAttempted = true;
        row.markAllAsTouched();
      });
      return;
    }

    const createRequests = this.rows.controls.map(row => {
      const value = row.getRawValue();
      const employmentHistory = this.getEmploymentHistory(row);

      return Object.assign(new CreateMemberRequest(), {
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        email: value.email.trim(),
        birthDate: this.formatDateOnlyForBackend(value.dateOfBirth),
        socialInsuranceNumber: value.socialInsuranceNumber.trim(),
        employmentHistory
      });
    });

    const verificationRequests = this.rows.controls.map(row => this.buildVerificationRequest(row));

    this.tableSubmitted.emit({
      createRequests,
      verificationRequests
    });
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
